import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState
} from 'react';
import axiosConfig from 'src/Resources/axiosConfig';
import { TokenContext } from './tokenContext';
import {
  I_UserWithInheritedAccess,
  InnovEnergyUser
} from '../interfaces/UserTypes';
import { useIntl } from 'react-intl';
import { I_Installation } from '../interfaces/InstallationTypes';

interface AccessContextProviderProps {
  fetchInstallationsForUser: (userId: number) => void;
  accessibleInstallationsForUser: I_Installation[];
  availableUsers: InnovEnergyUser[];
  fetchAvailableUsers: () => Promise<void>;
  allUsers: InnovEnergyUser[];
  fetchAllUsers: () => Promise<void>;
  usersWithDirectAccess: InnovEnergyUser[];
  fetchUsersWithDirectAccessForResource: (
    tempresourceType: string,
    id: number
  ) => void;
  usersWithInheritedAccess: I_UserWithInheritedAccess[];
  fetchUsersWithInheritedAccessForResource: (
    tempresourceType: string,
    id: number
  ) => void;
  error: boolean;
  setError: (value: boolean) => void;
  updated: boolean;
  setUpdated: (value: boolean) => void;
  updatedmessage: string;
  errormessage: string;
  setErrorMessage: (value: string) => void;
  setUpdatedMessage: (value: string) => void;
  RevokeAccessFromResource: (
    resourceType: string,
    id: number,
    resourceId: string,
    current_ResourceId: number,
    name: string
  ) => void;
}

export const AccessContext = createContext<AccessContextProviderProps>({
  fetchInstallationsForUser: () => Promise.resolve(),
  accessibleInstallationsForUser: [],
  availableUsers: [],
  fetchAvailableUsers: () => {
    return Promise.resolve();
  },
  allUsers: [],
  fetchAllUsers: () => {
    return Promise.resolve();
  },
  usersWithDirectAccess: [],
  fetchUsersWithDirectAccessForResource: () => Promise.resolve(),
  usersWithInheritedAccess: [],
  fetchUsersWithInheritedAccessForResource: () => Promise.resolve(),
  error: false,
  setError: () => {},
  updated: false,
  setUpdated: () => {},
  updatedmessage: '',
  errormessage: '',
  setErrorMessage: () => {},
  setUpdatedMessage: () => {},
  RevokeAccessFromResource: () => Promise.resolve()
});

const AccessContextProvider = ({ children }: { children: ReactNode }) => {
  const intl = useIntl();
  const [error, setError] = useState(false);
  const [errormessage, setErrorMessage] = useState('');
  const [updated, setUpdated] = useState(false);
  const [updatedmessage, setUpdatedMessage] = useState('');
  const tokencontext = useContext(TokenContext);
  const { removeToken } = tokencontext;
  const [usersWithDirectAccess, setUsersWithDirectAccess] = useState<
    InnovEnergyUser[]
  >([]);
  const [availableUsers, setAvailableUsers] = useState<InnovEnergyUser[]>([]);
  const [allUsers, setAllUsers] = useState<InnovEnergyUser[]>([]);
  const [accessibleInstallationsForUser, setAccessibleInstallationsForUser] =
    useState<I_Installation[]>([]);

  const [usersWithInheritedAccess, setUsersWithInheritedAccess] = useState<
    I_UserWithInheritedAccess[]
  >([]);

  const fetchUsersWithDirectAccessForResource = useCallback(
    async (tempresourceType: string, id: number) => {
      axiosConfig
        .get(`/GetUsersWithDirectAccess${tempresourceType}?id=${id}`)
        .then((response) => {
          if (response) {
            setUsersWithDirectAccess(response.data);
          }
        })
        .catch(() => {
          setError(true);
          setErrorMessage(intl.formatMessage({ id: 'unableToLoadData' }));
        });
    },
    [intl]
  );

  const fetchInstallationsForUser = useCallback(async (userId: number) => {
    axiosConfig
      .get(`/GetInstallationsTheUserHasAccess?userId=${userId}`)
      .then((response) => {
        if (response) {
          setAccessibleInstallationsForUser(response.data);
        }
      })
      .catch(() => {
        setError(true);
        setErrorMessage(intl.formatMessage({ id: 'unableToLoadData' }));
      });
  }, [intl]);

  const fetchUsersWithInheritedAccessForResource = useCallback(
    async (tempresourceType: string, id: number) => {
      axiosConfig
        .get(`/GetUsersWithInheritedAccess${tempresourceType}?id=${id}`)
        .then((response) => {
          if (response) {
            setUsersWithInheritedAccess(response.data);
          }
        })
        .catch(() => {
          setError(true);
          setErrorMessage(intl.formatMessage({ id: 'unableToLoadData' }));
        });
    },
    [intl]
  );

  const fetchAvailableUsers = async (): Promise<void> => {
    return axiosConfig.get('/GetAllChildUsers').then((res) => {
      setAvailableUsers(res.data);
    });
  };

  // Admin-only: every user in the system (for the Users management page).
  const fetchAllUsers = async (): Promise<void> => {
    return axiosConfig.get('/GetAllUsers').then((res) => {
      setAllUsers(res.data);
    });
  };

  const RevokeAccessFromResource = useCallback(
    async (
      resourceType: string,
      id: number,
      resourceId: string,
      current_ResourceId: number,
      name: string
    ) => {
      axiosConfig
        .post(
          `/RevokeUserAccess${resourceType}?UserId=${id}&${resourceId}=${current_ResourceId}`
        )
        .then((response) => {
          if (response) {
            fetchUsersWithDirectAccessForResource(
              resourceType,
              current_ResourceId
            );
            fetchUsersWithInheritedAccessForResource(
              resourceType,
              current_ResourceId
            );

            setUpdatedMessage(intl.formatMessage({ id: 'revokedAccessFromUser' }) + ' ' + name);

            setUpdated(true);
            setTimeout(() => {
              setUpdated(false);
            }, 3000);
          }
        })
        .catch(() => {
          setError(true);
          setErrorMessage(intl.formatMessage({ id: 'unableToRevokeAccess' }));
        });
    },
    [intl, fetchUsersWithDirectAccessForResource, fetchUsersWithInheritedAccessForResource]
  );

  return (
    <AccessContext.Provider
      value={{
        fetchInstallationsForUser,
        accessibleInstallationsForUser,
        availableUsers,
        fetchAvailableUsers,
        allUsers,
        fetchAllUsers,
        usersWithDirectAccess,
        fetchUsersWithDirectAccessForResource,
        usersWithInheritedAccess,
        fetchUsersWithInheritedAccessForResource,
        error,
        setError,
        updated,
        setUpdated,
        updatedmessage,
        errormessage,
        setErrorMessage,
        setUpdatedMessage,
        RevokeAccessFromResource
      }}
    >
      {children}
    </AccessContext.Provider>
  );
};

export default AccessContextProvider;
